import type maplibregl from "maplibre-gl";
import { useCallback, useEffect, useRef, useState } from "react";

import { BikeStationMapLayersController } from "./BikeStationMapLayersController";
import type { BikeStationClickPayload, BikeStationPopupState } from "./bikeStationMapLayers.types";

import {
    bikeStationPopupRequestSlice,
    useEphemeralSelector,
    useMapInstance,
} from "../../../store/ephemeral";
import { usePersistedSlice } from "../../../store/persistence";
import { rentalVehiclesPrefsSlice } from "../../../store/persistence/slices/rentalVehiclesPrefs.slice";
import { RentalVehicleType } from "../../../types/dtos/api/itinerary/itinerary-search-request.dto";

const INITIAL_POPUP_STATE: BikeStationPopupState = {
    "visible":        false,
    "lngLat":         [ 0, 0 ],
    "name":           "",
    "stationId":      "",
    "operator":       "",
    "operatorName":   "",
    "bikesAvailable": null,
    "capacity":       null,
    "x":              0,
    "y":              0,
};

interface UseBikeStationMapLayersOptions {
    "isDarkUi":         boolean;
    "isMapInitialized": boolean;
}

interface UseBikeStationMapLayersResult {
    "setupBikeStationLayers": ( map: maplibregl.Map ) => void;
    "bikeStationPopupProps":  BikeStationPopupState & { "onClose": () => void };
    "bikeStationPopupKey":    number;
    "openBikeStationPopup":   ( x: number, y: number, lat: number, lon: number, name: string ) => void;
}

export function useBikeStationMapLayers( options: UseBikeStationMapLayersOptions ): UseBikeStationMapLayersResult {
    const { isDarkUi, isMapInitialized } = options;

    const controllerRef = useRef( new BikeStationMapLayersController() );

    const [ popup, setPopup ]       = useState<BikeStationPopupState>( INITIAL_POPUP_STATE );
    const [ popupKey, setPopupKey ] = useState( 0 );

    const [ rentalPrefs ] = usePersistedSlice( rentalVehiclesPrefsSlice );
    const bikeStationsVisible = rentalPrefs.enabledTypes.includes( RentalVehicleType.BICYCLE );

    const map = useMapInstance();
    const popupRequest = useEphemeralSelector( bikeStationPopupRequestSlice, ( state ) => state );

    const handleClick = useCallback( ( payload: BikeStationClickPayload ): void => {
        setPopup( {
            "visible":        true,
            "lngLat":         payload.lngLat,
            "name":           payload.name,
            "stationId":      payload.stationId,
            "operator":       payload.operator,
            "operatorName":   payload.operatorName,
            "rentalUris":     payload.rentalUris,
            "bikesAvailable": payload.bikesAvailable ?? null,
            "capacity":       payload.capacity ?? null,
            "x":              payload.x,
            "y":              payload.y,
        } );
        setPopupKey( ( prev ) => prev + 1 );
    }, [] );

    const openBikeStationPopup = useCallback( (
        x: number,
        y: number,
        lat: number,
        lon: number,
        name: string,
    ): void => {
        setPopup( {
            "visible":        true,
            "lngLat":         [ lon, lat ],
            "name":           name,
            "stationId":      "",
            "operator":       "",
            "operatorName":   "",
            "bikesAvailable": null,
            "capacity":       null,
            "x":              x,
            "y":              y,
        } );
        setPopupKey( ( prev ) => prev + 1 );
    }, [] );

    const closePopup = useCallback( (): void => {
        setPopup( ( prev ) => ( {
            ...prev,
            "visible": false,
        } ) );
    }, [] );

    const setupBikeStationLayers = useCallback( ( map: maplibregl.Map ): void => {
        void controllerRef.current.attach( map, {
            "isDarkUi":           isDarkUi,
            "onBikeStationClick": handleClick,
        } ).then( () => {
            controllerRef.current.setVisibility( bikeStationsVisible );
        } );
    }, [ handleClick, bikeStationsVisible, isDarkUi ] );

    useEffect( () => () => {
        controllerRef.current.detach();
    }, [] );

    useEffect( () => {
        controllerRef.current.setVisibility( bikeStationsVisible );
    }, [ bikeStationsVisible ] );

    useEffect( () => {
        if ( !isMapInitialized ) {
            return;
        }

        void controllerRef.current.applyTheme( isDarkUi );
    }, [ isDarkUi, isMapInitialized ] );

    useEffect( () => {
        if ( !isMapInitialized || !popupRequest.visible || popupRequest.stationId === "" ) {
            return;
        }

        /*
         * Defer the popup state update to avoid `react-hooks/set-state-in-effect`.
         * The store request is the external event; the local popup state is updated
         * before the next paint.
         */
        queueMicrotask( () => {
            setPopup( {
                "visible":        true,
                "lngLat":         [ popupRequest.lon, popupRequest.lat ],
                "name":           popupRequest.name,
                "stationId":      popupRequest.stationId,
                "operator":       popupRequest.operator,
                "operatorName":   "",
                "bikesAvailable": null,
                "capacity":       null,
                "x":              0,
                "y":              0,
            } );
            setPopupKey( ( prev ) => prev + 1 );

            if ( map !== undefined ) {
                map.easeTo( {
                    "center":   [ popupRequest.lon, popupRequest.lat ],
                    "zoom":     16,
                    "duration": 700,
                } );
            }
        } );
    }, [ isMapInitialized, map, popupRequest ] );

    return {
        setupBikeStationLayers,
        "bikeStationPopupProps": {
            ...popup,
            "onClose": closePopup,
        },
        "bikeStationPopupKey": popupKey,
        openBikeStationPopup,
    };
}
